import { supabase } from './supabase';

export interface Admission {
  id: string;
  admission_no: string;
  patient_id: string;
  doctor_id: string;
  encounter_id?: string;
  admission_date: string;
  discharge_date?: string;
  admission_type: 'emergency' | 'planned' | 'transfer';
  status: 'admitted' | 'under_treatment' | 'ready_for_discharge' | 'discharged' | 'cancelled';
  chief_complaint?: string;
  provisional_diagnosis?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  patient?: any;
  doctor?: any;
  current_bed?: any;
}

export interface Ward {
  id: string;
  ward_no: string;
  name: string;
  department?: string;
  floor?: string;
  bed_capacity: number;
  active: boolean;
}

export interface Bed {
  id: string;
  bed_no: string;
  ward_id: string;
  bed_type: 'general' | 'semi_private' | 'private' | 'icu' | 'nicu' | 'emergency';
  daily_rate: number;
  status: 'available' | 'occupied' | 'cleaning' | 'maintenance' | 'reserved';
  active: boolean;
  ward?: Ward;
}

export interface BedAssignment {
  id: string;
  admission_id: string;
  bed_id: string;
  assigned_at: string;
  released_at?: string;
  notes?: string;
  bed?: Bed;
}

export interface DischargeSummary {
  id: string;
  admission_id: string;
  summary_no: string;
  discharge_date: string;
  discharged_by: string;
  admission_diagnosis?: string;
  final_diagnosis?: string;
  treatment_summary?: string;
  procedures_performed?: string;
  medications_prescribed?: string;
  follow_up_instructions?: string;
  diet_restrictions?: string;
  activity_restrictions?: string;
  follow_up_date?: string;
  pdf_url?: string;
  verification_token?: string;
  created_at: string;
  updated_at: string;
}

export const admissionService = {
  async getAll(): Promise<Admission[]> {
    const { data, error } = await supabase
      .from('admissions')
      .select(`
        *,
        patient:patients(id, name, email, phone, date_of_birth),
        doctor:doctors(id, name, specialization)
      `)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<Admission | null> {
    const { data, error } = await supabase
      .from('admissions')
      .select(`
        *,
        patient:patients(id, name, email, phone, date_of_birth),
        doctor:doctors(id, name, specialization)
      `)
      .eq('id', id)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  },

  async create(admission: Omit<Admission, 'id' | 'admission_no' | 'created_at' | 'updated_at'>): Promise<Admission> {
    const { data, error } = await supabase
      .from('admissions')
      .insert(admission)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateStatus(id: string, status: Admission['status']): Promise<Admission> {
    const { data, error } = await supabase
      .from('admissions')
      .update({ 
        status,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async getActive(): Promise<Admission[]> {
    const { data, error } = await supabase
      .from('admissions')
      .select(`
        *,
        patient:patients(id, name),
        doctor:doctors(id, name)
      `)
      .in('status', ['admitted', 'under_treatment'])
      .order('admission_date', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },
};

export const wardService = {
  async getAll(): Promise<Ward[]> {
    const { data, error } = await supabase
      .from('wards')
      .select('*')
      .eq('active', true)
      .order('ward_no');
    
    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<Ward | null> {
    const { data, error } = await supabase
      .from('wards')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  },

  async create(ward: Omit<Ward, 'id' | 'created_at' | 'updated_at'>): Promise<Ward> {
    const { data, error } = await supabase
      .from('wards')
      .insert(ward)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },
};

export const bedService = {
  async getAll(): Promise<Bed[]> {
    const { data, error } = await supabase
      .from('beds')
      .select('*, ward:wards(*)')
      .eq('active', true)
      .order('bed_no');
    
    if (error) throw error;
    return data || [];
  },

  async getAvailable(): Promise<Bed[]> {
    const { data, error } = await supabase
      .from('beds')
      .select('*, ward:wards(*)')
      .eq('active', true)
      .eq('status', 'available')
      .order('bed_no');
    
    if (error) throw error;
    return data || [];
  },

  async assignBed(admissionId: string, bedId: string, notes?: string): Promise<BedAssignment> {
    const { data, error } = await supabase
      .from('admission_bed_assignments')
      .insert({
        admission_id: admissionId,
        bed_id: bedId,
        notes
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async releaseBed(assignmentId: string): Promise<BedAssignment> {
    const { data, error } = await supabase
      .from('admission_bed_assignments')
      .update({ released_at: new Date().toISOString() })
      .eq('id', assignmentId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async getCurrentAssignment(admissionId: string): Promise<BedAssignment | null> {
    const { data, error } = await supabase
      .from('admission_bed_assignments')
      .select('*, bed:beds(*, ward:wards(*))')
      .eq('admission_id', admissionId)
      .is('released_at', null)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  },

  async create(bed: Omit<Bed, 'id' | 'ward' | 'created_at' | 'updated_at'>): Promise<Bed> {
    const { data, error } = await supabase
      .from('beds')
      .insert(bed)
      .select('*, ward:wards(*)')
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(id: string, bed: Partial<Omit<Bed, 'id' | 'ward' | 'created_at' | 'updated_at'>>): Promise<Bed> {
    const { data, error } = await supabase
      .from('beds')
      .update({
        ...bed,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select('*, ward:wards(*)')
      .single();
    
    if (error) throw error;
    return data;
  },

  async retire(id: string): Promise<void> {
    const { data: assignments, error: assignError } = await supabase
      .from('admission_bed_assignments')
      .select('id')
      .eq('bed_id', id)
      .is('released_at', null)
      .limit(1);
    
    if (assignError) throw assignError;
    
    if (assignments && assignments.length > 0) {
      throw new Error('Cannot retire an occupied bed');
    }
    
    const { error } = await supabase
      .from('beds')
      .update({ active: false, updated_at: new Date().toISOString() })
      .eq('id', id);
    
    if (error) throw error;
  },

  async checkBedNoUnique(bedNo: string, wardId: string, excludeBedId?: string): Promise<boolean> {
    let query = supabase
      .from('beds')
      .select('id')
      .eq('bed_no', bedNo)
      .eq('ward_id', wardId);
    
    if (excludeBedId) {
      query = query.neq('id', excludeBedId);
    }
    
    const { data, error } = await query.limit(1);
    
    if (error) throw error;
    return !data || data.length === 0;
  },
};

export const dischargeSummaryService = {
  async getByAdmission(admissionId: string): Promise<DischargeSummary | null> {
    const { data, error } = await supabase
      .from('discharge_summaries')
      .select('*')
      .eq('admission_id', admissionId)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  },

  async create(summary: Omit<DischargeSummary, 'id' | 'summary_no' | 'created_at' | 'updated_at'>): Promise<DischargeSummary> {
    const { data: userData } = await supabase.auth.getUser();
    const verificationToken = Math.random().toString(36).substring(2, 15);
    
    const { data, error } = await supabase
      .from('discharge_summaries')
      .insert({
        ...summary,
        discharged_by: userData?.user?.id,
        verification_token: verificationToken,
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async uploadPDF(summaryId: string, pdfBlob: Blob): Promise<string> {
    const fileName = `discharge-${summaryId}-${Date.now()}.pdf`;
    const { data, error } = await supabase.storage
      .from('discharge-summaries')
      .upload(fileName, pdfBlob, {
        contentType: 'application/pdf',
        upsert: false
      });
    
    if (error) throw error;
    return fileName;
  },

  async getSignedUrl(fileName: string, expiresIn = 3600): Promise<string> {
    const { data, error } = await supabase.storage
      .from('discharge-summaries')
      .createSignedUrl(fileName, expiresIn);
    
    if (error) throw error;
    return data.signedUrl;
  },
};

export interface NursingNote {
  id: string;
  admission_id: string;
  note_date: string;
  shift: 'morning' | 'evening' | 'night';
  nurse_id: string;
  note: string;
  created_at: string;
  updated_at: string;
  nurse?: any;
}

export interface DoctorProgressNote {
  id: string;
  admission_id: string;
  doctor_id: string;
  note_date: string;
  diagnosis?: string;
  plan?: string;
  remarks?: string;
  created_at: string;
  updated_at: string;
  doctor?: any;
}

export interface VitalsRecord {
  id: string;
  admission_id: string;
  recorded_at: string;
  nurse_id: string;
  temperature?: number;
  pulse?: number;
  blood_pressure?: string;
  respiration?: number;
  spo2?: number;
  notes?: string;
  created_at: string;
  nurse?: any;
}

export interface MedicationAdministration {
  id: string;
  admission_id: string;
  medicine_name: string;
  dose: string;
  route: 'oral' | 'iv' | 'im' | 'subcutaneous' | 'topical';
  frequency: string;
  given_at: string;
  nurse_id: string;
  remarks?: string;
  created_at: string;
  nurse?: any;
}

export const nursingNotesService = {
  async getByAdmission(admissionId: string): Promise<NursingNote[]> {
    const { data, error } = await supabase
      .from('nursing_notes')
      .select('*, nurse:users(id, email)')
      .eq('admission_id', admissionId)
      .order('note_date', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async create(note: Omit<NursingNote, 'id' | 'created_at' | 'updated_at'>): Promise<NursingNote> {
    const { data: userData } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('nursing_notes')
      .insert({
        ...note,
        nurse_id: userData?.user?.id,
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(id: string, note: Partial<NursingNote>): Promise<NursingNote> {
    const { data, error } = await supabase
      .from('nursing_notes')
      .update({
        ...note,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },
};

export const doctorProgressNotesService = {
  async getByAdmission(admissionId: string): Promise<DoctorProgressNote[]> {
    const { data, error } = await supabase
      .from('doctor_progress_notes')
      .select('*, doctor:users(id, email)')
      .eq('admission_id', admissionId)
      .order('note_date', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async create(note: Omit<DoctorProgressNote, 'id' | 'created_at' | 'updated_at'>): Promise<DoctorProgressNote> {
    const { data: userData } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('doctor_progress_notes')
      .insert({
        ...note,
        doctor_id: userData?.user?.id,
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(id: string, note: Partial<DoctorProgressNote>): Promise<DoctorProgressNote> {
    const { data, error } = await supabase
      .from('doctor_progress_notes')
      .update({
        ...note,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },
};

export const vitalsService = {
  async getByAdmission(admissionId: string): Promise<VitalsRecord[]> {
    const { data, error } = await supabase
      .from('vitals_records')
      .select('*, nurse:users(id, email)')
      .eq('admission_id', admissionId)
      .order('recorded_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async create(vitals: Omit<VitalsRecord, 'id' | 'created_at'>): Promise<VitalsRecord> {
    const { data: userData } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('vitals_records')
      .insert({
        ...vitals,
        nurse_id: userData?.user?.id,
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },
};

export const medicationAdministrationService = {
  async getByAdmission(admissionId: string): Promise<MedicationAdministration[]> {
    const { data, error } = await supabase
      .from('medication_administration')
      .select('*, nurse:users(id, email)')
      .eq('admission_id', admissionId)
      .order('given_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async markAsGiven(medication: Omit<MedicationAdministration, 'id' | 'created_at'>): Promise<MedicationAdministration> {
    const { data: userData } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('medication_administration')
      .insert({
        ...medication,
        nurse_id: userData?.user?.id,
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },
};
export interface AdmissionTransfer {
  id: string;
  admission_id: string;
  from_bed_id?: string;
  to_bed_id?: string;
  from_ward_name?: string;
  to_ward_name?: string;
  reason?: string;
  transferred_by?: string;
  transferred_at: string;
  created_at: string;
}

export interface ConsentTemplate {
  id: string;
  name: string;
  body: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Consent {
  id: string;
  admission_id: string;
  template_id?: string;
  template_name?: string;
  filled_body: string;
  signed_by: string;
  signed_at: string;
  signature_url?: string;
  created_by?: string;
  created_at: string;
}

export interface BirthDeathCertificate {
  id: string;
  admission_id: string;
  certificate_type: 'birth' | 'death';
  certificate_no: string;
  patient_name: string;
  details: any;
  issued_by?: string;
  issued_at: string;
  pdf_url?: string;
  created_at: string;
}

export interface Surgery {
  id: string;
  admission_id: string;
  surgery_no?: string;
  scheduled_at?: string;
  started_at?: string;
  completed_at?: string;
  surgeon_id?: string;
  ot_room?: string;
  procedure_name: string;
  anesthesia_type?: string;
  findings?: string;
  complications?: string;
  post_op_plan?: string;
  estimated_cost?: number;
  actual_cost?: number;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  pdf_url?: string;
  created_at: string;
  updated_at: string;
  surgeon?: any;
}

export const autoAllocationService = {
  async suggestBed(wardId?: string, bedType?: string): Promise<Bed | null> {
    let query = supabase
      .from('beds')
      .select('*, ward:wards(*)')
      .eq('active', true)
      .eq('status', 'available')
      .order('daily_rate', { ascending: true });
    
    if (wardId) {
      query = query.eq('ward_id', wardId);
    }
    
    if (bedType) {
      query = query.eq('bed_type', bedType);
    }
    
    const { data, error } = await query.limit(1).single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  },

  async autoAssignBed(admissionId: string, bedId: string, notes?: string): Promise<BedAssignment> {
    await supabase
      .from('beds')
      .update({ status: 'occupied' })
      .eq('id', bedId);
    
    return bedService.assignBed(admissionId, bedId, notes);
  },
};

export const transferService = {
  async getByAdmission(admissionId: string): Promise<AdmissionTransfer[]> {
    const { data, error } = await supabase
      .from('admission_transfers')
      .select('*')
      .eq('admission_id', admissionId)
      .order('transferred_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async transferBed(params: {
    admissionId: string;
    toBedId: string;
    reason?: string;
  }): Promise<AdmissionTransfer> {
    const { admissionId, toBedId, reason } = params;
    const { data: userData } = await supabase.auth.getUser();
    
    const currentAssignment = await bedService.getCurrentAssignment(admissionId);
    
    let fromBedId: string | undefined;
    let fromWardName: string | undefined;
    
    if (currentAssignment) {
      fromBedId = currentAssignment.bed_id;
      fromWardName = currentAssignment.bed?.ward?.name;
      
      await bedService.releaseBed(currentAssignment.id);
      await supabase
        .from('beds')
        .update({ status: 'cleaning' })
        .eq('id', currentAssignment.bed_id);
    }
    
    const { data: newBed } = await supabase
      .from('beds')
      .select('*, ward:wards(*)')
      .eq('id', toBedId)
      .single();
    
    await bedService.assignBed(admissionId, toBedId);
    await supabase
      .from('beds')
      .update({ status: 'occupied' })
      .eq('id', toBedId);
    
    const { data, error } = await supabase
      .from('admission_transfers')
      .insert({
        admission_id: admissionId,
        from_bed_id: fromBedId,
        to_bed_id: toBedId,
        from_ward_name: fromWardName,
        to_ward_name: newBed?.ward?.name,
        reason,
        transferred_by: userData?.user?.id,
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },
};

export const consentService = {
  async getTemplates(): Promise<ConsentTemplate[]> {
    const { data, error } = await supabase
      .from('consent_templates')
      .select('*')
      .eq('active', true)
      .order('name');
    
    if (error) throw error;
    return data || [];
  },

  async getByAdmission(admissionId: string): Promise<Consent[]> {
    const { data, error } = await supabase
      .from('consents')
      .select('*')
      .eq('admission_id', admissionId)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  renderTemplate(template: string, context: Record<string, string>): string {
    let rendered = template;
    Object.entries(context).forEach(([key, value]) => {
      rendered = rendered.replace(new RegExp(`{{${key}}}`, 'g'), value);
    });
    return rendered;
  },

  async create(consent: Omit<Consent, 'id' | 'created_at' | 'created_by'>): Promise<Consent> {
    const { data: userData } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('consents')
      .insert({
        ...consent,
        created_by: userData?.user?.id,
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },
};

export const certificateService = {
  async getByAdmission(admissionId: string): Promise<BirthDeathCertificate[]> {
    const { data, error } = await supabase
      .from('birth_death_certificates')
      .select('*')
      .eq('admission_id', admissionId)
      .order('created_at', { ascending: false});
    
    if (error) throw error;
    return data || [];
  },

  async create(certificate: Omit<BirthDeathCertificate, 'id' | 'certificate_no' | 'created_at'>): Promise<BirthDeathCertificate> {
    const { data: userData } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('birth_death_certificates')
      .insert({
        ...certificate,
        issued_by: userData?.user?.id,
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async uploadPDF(certificateId: string, pdfBlob: Blob): Promise<string> {
    const fileName = `certificate-${certificateId}-${Date.now()}.pdf`;
    const { data, error } = await supabase.storage
      .from('certificates')
      .upload(fileName, pdfBlob, {
        contentType: 'application/pdf',
        upsert: false
      });
    
    if (error) throw error;
    
    await supabase
      .from('birth_death_certificates')
      .update({ pdf_url: fileName })
      .eq('id', certificateId);
    
    return fileName;
  },

  async getSignedUrl(fileName: string, expiresIn = 3600): Promise<string> {
    const { data, error } = await supabase.storage
      .from('certificates')
      .createSignedUrl(fileName, expiresIn);
    
    if (error) throw error;
    return data.signedUrl;
  },
};

export const surgeryService = {
  async getByAdmission(admissionId: string): Promise<Surgery[]> {
    const { data, error } = await supabase
      .from('surgeries')
      .select('*, surgeon:users(id, email)')
      .eq('admission_id', admissionId)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<Surgery | null> {
    const { data, error } = await supabase
      .from('surgeries')
      .select('*, surgeon:users(id, email)')
      .eq('id', id)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  },

  async create(surgery: Omit<Surgery, 'id' | 'surgery_no' | 'created_at' | 'updated_at' | 'surgeon'>): Promise<Surgery> {
    const { data: userData } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('surgeries')
      .insert({
        ...surgery,
        surgeon_id: surgery.surgeon_id || userData?.user?.id,
      })
      .select('*, surgeon:users(id, email)')
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(id: string, surgery: Partial<Surgery>): Promise<Surgery> {
    const { data, error } = await supabase
      .from('surgeries')
      .update({
        ...surgery,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select('*, surgeon:users(id, email)')
      .single();
    
    if (error) throw error;
    return data;
  },

  async uploadPDF(surgeryId: string, pdfBlob: Blob): Promise<string> {
    const fileName = `surgery-${surgeryId}-${Date.now()}.pdf`;
    const { data, error } = await supabase.storage
      .from('surgery-notes')
      .upload(fileName, pdfBlob, {
        contentType: 'application/pdf',
        upsert: false
      });
    
    if (error) throw error;
    
    await supabase
      .from('surgeries')
      .update({ pdf_url: fileName })
      .eq('id', surgeryId);
    
    return fileName;
  },
};
